/**
 * 이미지 한 장에서 "이게 어떤 재질인가"를 추론하기 위한 통계와 팔레트.
 *
 * 여기서 나온 숫자들이 프리셋 자동 선택과 러프니스/메탈니스 기본값을 정한다.
 * 정확한 물리량이 아니라 재질의 **성향**을 잡는 게 목적이다.
 */

import type { Field, ImageAnalysis, ImageStats, PaletteEntry, Raster } from '../types.ts';
import {
  laplacian,
  luminanceField,
  resampleField,
  saturationAt,
  gaussianBlur,
} from './raster.ts';
import { distanceTransform, extractSilhouette } from './silhouette.ts';

/** OKLab 근사 — 지각적으로 균등해서 k-means 군집이 사람 눈과 비슷하게 갈린다. */
function srgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const lr = r / 255, lg = g / 255, lb = b / 255;
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

interface Sample {
  lab: [number, number, number];
  rgb: [number, number, number];
}

function collectSamples(src: Raster, mask: Field, limit = 16000): Sample[] {
  const total = src.width * src.height;
  const stride = Math.max(1, Math.floor(total / limit));
  const out: Sample[] = [];
  for (let i = 0; i < total; i += stride) {
    if (mask.data[i]! < 0.5) continue;
    const p = i * 4;
    if (src.data[p + 3]! < 128) continue;
    const rgb: [number, number, number] = [src.data[p]!, src.data[p + 1]!, src.data[p + 2]!];
    out.push({ rgb, lab: srgbToOklab(rgb[0], rgb[1], rgb[2]) });
  }
  return out;
}

/** k-means++ 초기화 + 로이드 반복 12회. 이 정도면 팔레트는 안정된다. */
export function extractPalette(src: Raster, mask: Field, k = 6): PaletteEntry[] {
  const samples = collectSamples(src, mask);
  if (samples.length === 0) return [{ rgb: [128, 128, 128], weight: 1 }];
  const kk = Math.min(k, samples.length);

  const dist2 = (a: [number, number, number], b: [number, number, number]) =>
    (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

  const centers: [number, number, number][] = [samples[0]!.lab];
  const best = new Float64Array(samples.length).fill(Infinity);
  for (let c = 1; c < kk; c++) {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      const d = dist2(samples[i]!.lab, centers[c - 1]!);
      if (d < best[i]!) best[i] = d;
      sum += best[i]!;
    }
    let target = Math.random() * sum;
    let pick = samples.length - 1;
    for (let i = 0; i < samples.length; i++) {
      target -= best[i]!;
      if (target <= 0) { pick = i; break; }
    }
    centers.push(samples[pick]!.lab);
  }

  const assign = new Int32Array(samples.length);
  for (let iter = 0; iter < 12; iter++) {
    let moved = 0;
    for (let i = 0; i < samples.length; i++) {
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let c = 0; c < centers.length; c++) {
        const d = dist2(samples[i]!.lab, centers[c]!);
        if (d < bestDist) { bestDist = d; bestIdx = c; }
      }
      if (assign[i] !== bestIdx) { assign[i] = bestIdx; moved++; }
    }
    const sums = centers.map(() => [0, 0, 0, 0]);
    for (let i = 0; i < samples.length; i++) {
      const s = sums[assign[i]!]!;
      s[0]! += samples[i]!.lab[0];
      s[1]! += samples[i]!.lab[1];
      s[2]! += samples[i]!.lab[2];
      s[3]! += 1;
    }
    for (let c = 0; c < centers.length; c++) {
      const s = sums[c]!;
      if (s[3]! > 0) centers[c] = [s[0]! / s[3]!, s[1]! / s[3]!, s[2]! / s[3]!];
    }
    if (moved === 0) break;
  }

  // 대표색은 군집 중심에 가장 가까운 실제 픽셀로 고른다 — 평균색은 탁해진다.
  const counts = new Array(centers.length).fill(0);
  const repDist = new Array(centers.length).fill(Infinity);
  const repRgb: [number, number, number][] = centers.map(() => [128, 128, 128]);
  for (let i = 0; i < samples.length; i++) {
    const c = assign[i]!;
    counts[c]++;
    const d = dist2(samples[i]!.lab, centers[c]!);
    if (d < repDist[c]) { repDist[c] = d; repRgb[c] = samples[i]!.rgb; }
  }

  return centers
    .map((_, c) => ({ rgb: repRgb[c]!, weight: counts[c] / samples.length }))
    .filter((e) => e.weight > 0.004)
    .sort((a, b) => b.weight - a.weight);
}

export function computeStats(src: Raster, mask: Field, luma: Field): ImageStats {
  const n = src.width * src.height;
  let count = 0;
  let sumL = 0, sumL2 = 0, sumSat = 0;
  let spec = 0, shadow = 0;

  for (let i = 0, p = 0; i < n; i++, p += 4) {
    if (mask.data[i]! < 0.5) continue;
    count++;
    const l = luma.data[i]!;
    sumL += l;
    sumL2 += l * l;
    const sat = saturationAt(src.data, p);
    sumSat += sat;
    if (l > 0.78 && sat < 0.22) spec++;
    if (l < 0.06) shadow++;
  }
  if (count === 0) count = 1;

  const meanLuma = sumL / count;
  const variance = Math.max(0, sumL2 / count - meanLuma * meanLuma);

  // 디테일: 라플라시안 절댓값의 마스크 내 평균.
  // 라플라시안은 해상도를 탄다 — 같은 그림도 768px 이면 512px 보다 값이 작다.
  // 프리셋 문턱값을 512 기준으로 잡았으니 항상 그 크기로 줄여서 잰다.
  const scale = 512 / Math.max(src.width, src.height);
  const sw = Math.max(8, Math.round(src.width * scale));
  const sh = Math.max(8, Math.round(src.height * scale));
  const smallLuma = resampleField(luma, sw, sh);
  const smallMask = resampleField(mask, sw, sh);
  const lap = laplacian(smallLuma);
  let detail = 0;
  let dCount = 0;
  for (let i = 0; i < lap.data.length; i++) {
    if (smallMask.data[i]! < 0.5) continue;
    detail += Math.abs(lap.data[i]!);
    dCount++;
  }

  return {
    meanLuma,
    contrast: Math.sqrt(variance),
    meanSaturation: sumSat / count,
    detail: dCount ? detail / dCount : 0,
    specularRatio: spec / count,
    shadowRatio: shadow / count,
  };
}

export interface AnalyzeOptions {
  /** 실루엣을 밖에서 주면(캐릭터 시트) 추출을 건너뛴다. */
  mask?: Field;
}

export function analyzeImage(src: Raster, opts: AnalyzeOptions = {}): ImageAnalysis {
  const { mask, source, coverage } = opts.mask
    ? { mask: opts.mask, source: 'flood' as const, coverage: coverageOf(opts.mask) }
    : extractSilhouette(src);
  const luma = luminanceField(src);
  const distance = distanceTransform(mask);
  // 거리장을 조금 눌러 주면 부풀린 표면이 원뿔 대신 둥글게 나온다.
  const smoothDistance = gaussianBlur(distance, Math.max(1, Math.max(src.width, src.height) / 250));

  return {
    source: src,
    mask,
    distance: smoothDistance,
    luma,
    palette: extractPalette(src, mask),
    stats: computeStats(src, mask, luma),
    coverage,
    maskSource: source,
  };
}

function coverageOf(mask: Field): number {
  let n = 0;
  for (let i = 0; i < mask.data.length; i++) if (mask.data[i]! >= 0.5) n++;
  return n / mask.data.length;
}
